import { Children, useCallback, useContext, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import UITourStep from 'ui-shepherd-react/tour/UITourStep';
import UITourNextButton from 'ui-shepherd-react/button/UITourNextButton';
import UIButton from 'UIComponents/button/UIButton';
import { DONE } from 'ui-shepherd-react/constants/TourStates';
import { TOUR_POPOVER_BOTTOM_MARGIN, TOUR_POPOVER_WIDTH_PX } from '../constants/UIConstants';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getOnboardingStep } from '../constants/Events';
import { useOnboardingSteps } from '../hooks/useOnboardingSteps';
import { canRenderTourStep } from '../operators/canRenderTourStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LastStepFooter = ({
  handleTourComplete
}) => {
  return /*#__PURE__*/_jsx(UITourNextButton, {
    beforeNext: handleTourComplete,
    use: "primary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "conversations-inbox-ui.new-feature-onboarding.finishTour"
    })
  });
};
LastStepFooter.displayName = 'LastStepFooter';
const Footer = ({
  handleSkipTour
}) => {
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(UITourNextButton, {
      use: "primary",
      "data-test-id": "ui-tour-next-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-inbox-ui.new-feature-onboarding.nextTip"
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      onClick: handleSkipTour,
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-inbox-ui.new-feature-onboarding.skipTour"
      })
    })]
  });
};
Footer.displayName = 'Footer';
const NewFeatureTourStep = ({
  className = '',
  stepKey,
  header,
  body,
  footer,
  onClickCloseButton,
  placement,
  width,
  children,
  showCloseButton,
  stepText
}) => {
  const {
    hasUserSeenOnboarding,
    unseenSteps
  } = useOnboardingSteps();
  const {
    tour
  } = useContext(TourContext);
  const [dimensions, updateDimensions] = useState({});
  const childRef = useCallback(node => {
    if (node !== null) {
      updateDimensions({
        height: node.getBoundingClientRect().height,
        width: node.getBoundingClientRect().width
      });
    }
  }, []);
  if (hasUserSeenOnboarding) {
    return children;
  }
  const handleBeforeStart = () => {
    trackEvent(getOnboardingStep({
      action: 'tour step',
      stepName: stepKey
    }));
  };
  const handleSkipTour = () => {
    trackEvent(getOnboardingStep({
      action: 'skipped tour',
      stepName: stepKey
    }));
    tour.finish();
  };
  const handleTourComplete = () => {
    trackEvent(getOnboardingStep({
      action: 'tour end',
      stepName: DONE
    }));
  };
  const isLastStep = unseenSteps ?
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
  unseenSteps[unseenSteps.indexOf(stepKey) + 1] === DONE : false;
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    style: {
      position: 'relative'
    },
    children: [canRenderTourStep(tour) && /*#__PURE__*/_jsx(UITourStep, {
      className: "new-feature-tour-step",
      beforeStart: handleBeforeStart,
      stepKey: stepKey,
      content: {
        header: /*#__PURE__*/_jsx("h4", {
          children: header
        }),
        body: /*#__PURE__*/_jsx("div", {
          style: {
            marginBottom: TOUR_POPOVER_BOTTOM_MARGIN
          },
          children: /*#__PURE__*/_jsx("p", {
            children: body
          })
        }),
        footer: footer || (isLastStep ? /*#__PURE__*/_jsx(LastStepFooter, {
          handleTourComplete: handleTourComplete
        }) : /*#__PURE__*/_jsx(Footer, {
          handleSkipTour: handleSkipTour
        })),
        step: stepText
      },
      onClickCloseButton: onClickCloseButton
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '"left" | ... Remove this comment to see the full error message
      ,
      placement: placement,
      showCloseButton: showCloseButton,
      width: width || TOUR_POPOVER_WIDTH_PX,
      children: /*#__PURE__*/_jsx("div", {
        style: {
          height: dimensions.height,
          width: dimensions.width,
          position: 'absolute'
        }
      })
    }), /*#__PURE__*/_jsx("div", {
      ref: childRef,
      style: {
        /**
         * A non-default opacity rule is required for displaying this sibling element
         * above the absolutely-positioned sibling (above).
         *
         * See https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
         */
        opacity: 0.99
      },
      children: Children.only(children)
    })]
  });
};
NewFeatureTourStep.displayName = 'NewFeatureTourStep';
export default NewFeatureTourStep;